// Phase 24b · Upsell banner (universal component).
// Props: surface (required, one of ALL_SURFACES from backend).
// - Fetches /api/upsell/suggestion on mount + on visibility resume (30s coalescing).
// - Renders nothing when null or when API returns 503 (kill-switch OFF).
// - Click tracked via POST /api/upsell/events/click before navigating to /pricing?src=upsell_<slug>.
// - X (dismiss) tracked via POST /api/upsell/events/dismiss (hides same cta_slug 7d server-side).
import React, { useEffect, useState, useCallback, useRef } from "react";
import { Zap, X, ArrowRight } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function UpsellBanner({ surface }) {
    const [suggestion, setSuggestion] = useState(null);
    const [hidden, setHidden] = useState(false);
    const lastFetchRef = useRef(0);
    const inflightRef = useRef(false);

    const fetchSuggestion = useCallback(async () => {
        // 30s coalescing between fetches
        const now = Date.now();
        if (now - lastFetchRef.current < 30000) return;
        if (inflightRef.current) return;
        inflightRef.current = true;
        try {
            const r = await fetch(`${API}/api/upsell/suggestion?surface=${encodeURIComponent(surface)}`, {
                credentials: "include",
            });
            lastFetchRef.current = now;
            if (r.status === 503 || r.status === 401) { setSuggestion(null); return; }
            if (r.ok) {
                const d = await r.json();
                // API returns { suggestion: null } or the full CTA object
                setSuggestion(d && d.cta_slug ? d : null);
            }
        } catch {}
        inflightRef.current = false;
    }, [surface]);

    useEffect(() => {
        fetchSuggestion();
        const onVis = () => {
            if (document.visibilityState === "visible") fetchSuggestion();
        };
        document.addEventListener("visibilitychange", onVis);
        return () => document.removeEventListener("visibilitychange", onVis);
    }, [fetchSuggestion]);

    const trackAndGo = async () => {
        if (!suggestion) return;
        try {
            await fetch(`${API}/api/upsell/events/click`, {
                method: "POST",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    event_id: suggestion.event_id,
                    cta_slug: suggestion.cta_slug,
                    surface,
                }),
            });
        } catch {}
        window.location.href = `/pricing?src=upsell_${encodeURIComponent(suggestion.cta_slug)}&impression=${encodeURIComponent(suggestion.event_id || "")}`;
    };

    const dismiss = async () => {
        if (!suggestion) return;
        setHidden(true);
        try {
            await fetch(`${API}/api/upsell/events/dismiss`, {
                method: "POST",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    event_id: suggestion.event_id,
                    cta_slug: suggestion.cta_slug,
                    surface,
                }),
            });
        } catch {}
    };

    if (!suggestion || hidden) return null;

    return (
        <div
            data-testid={`upsell-banner-${surface}`}
            data-cta-slug={suggestion.cta_slug}
            data-cta-variant={suggestion.cta_variant}
            className="mb-4 rounded-xl border border-neutral-500/30 bg-gradient-to-r from-neutral-500/10 to-neutral-500/10 px-4 py-3 flex items-center gap-3"
        >
            <span
                data-testid={`upsell-banner-${surface}-tag`}
                className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-gradient-to-r from-neutral-500 to-neutral-500 text-white font-semibold shrink-0"
            >
                <Zap size={10} /> Upgrade
            </span>
            <p className="text-xs text-neutral-200 flex-1 leading-snug" data-testid={`upsell-banner-${surface}-text`}>
                {suggestion.cta_text}
            </p>
            <button
                onClick={trackAndGo}
                data-testid={`upsell-banner-${surface}-cta`}
                className="text-xs px-3 py-1.5 rounded-full bg-gradient-to-r from-neutral-500 to-neutral-500 text-white font-medium inline-flex items-center gap-1 shrink-0 hover:opacity-90"
            >
                {suggestion.cta_label || "Upgrade to Premium"} <ArrowRight size={12} />
            </button>
            <button
                onClick={dismiss}
                data-testid={`upsell-banner-${surface}-dismiss`}
                aria-label="Dismiss"
                className="text-neutral-500 hover:text-neutral-200 shrink-0"
            >
                <X size={14} />
            </button>
        </div>
    );
}
